import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  Grid,
  Stack,
  Link,
  Divider,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import GroupIcon from '@mui/icons-material/Group';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import 'react-dropzone-uploader/dist/styles.css';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import tracker from '../../../api/tracker';
import { PinDialog } from '../../../components/general/PinDialog';
import useNotification from '../../../hooks/useNotificaction';

// ----------------------------------------------------------------------

export default function AttendanceForm({ attendance }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendNotification] = useNotification();
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinDialogMessage, setPinDialogMessage] = useState(false);
  const [massDate, setMassDate] = useState(null);

  const ModelSchema = Yup.object().shape({
    attendance: Yup.string().required(t('Required')),
    date: Yup.string().required(t('Required')),
  });

  const defaultValues = {
    attendance: '',
    info: '',
    date: '',
  };

  const methods = useForm({
    resolver: yupResolver(ModelSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (attendance) {
      const fields = ['attendance', 'info', 'date'];
      fields.forEach((field) => setValue(field, attendance[field]));
      if (attendance.date) {
        setMassDate(dayjs(attendance.date));
      }
    }
  }, [attendance]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (datee) => {
    if (!datee) return '';

    const date = new Date(datee);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const onSubmit = async (data) => {
    let response;

    try {
      if (attendance?.hashid) {
        response = await tracker.post(`attendance/${attendance.hashid}`, data);
      } else {
        response = await tracker.post('attendance', data);
      }
    } catch (error) {
      if (error?.response?.status === 423) {
        setPinDialogMessage(error.response.data.message);
        setShowPinDialog(true);
        return;
      }

      sendNotification({ msg: t('something_went_wrong'), variant: 'error' });
      return;
    }

    // Handle PIN dialog required case (LOCKED/admin PIN required)
    if (![200, 201].includes(response?.status)) return;

    sendNotification({ msg: t('attendances_saved'), variant: 'success' });

    // Navegar al listado de asistencias
    navigate('/dashboard/attendance/', { replace: true });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        {/* LEFT: Main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Attendance count field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <GroupIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('Attendances')}</Typography>
              </Stack>
              <RHFTextField
                name="attendance"
                label={t('Attendances')}
                fullWidth
                type="number"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Box>

            {/* Mass Day / DateTime field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('Mass_day')}</Typography>
              </Stack>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="date"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <MobileDateTimePicker
                      label={t('Mass_day')}
                      value={field.value ? dayjs(field.value) : massDate}
                      onChange={(newValue) => {
                        field.onChange(formatDate(newValue));
                        setMassDate(newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            {/* Details field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('details')}</Typography>
              </Stack>
              <RHFTextField name="info" label={t('details')} fullWidth multiline minRows={3} />
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT: Actions panel */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              background: '#f8faff',
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {t('actions') || 'Acciones'}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {attendance && (
              <Box mb={2} p={1.5} sx={{ background: '#fff', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {t('attendance_info') || 'Información de asistencia'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {attendance.date ? (
                    <>{t('Mass_day')}: {attendance.date}</>
                  ) : (
                    t('no_info') || 'Sin información'
                  )}
                </Typography>
              </Box>
            )}

            <Stack spacing={1.5}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ background: '#032C65', '&:hover': { background: '#054a9e' }, borderRadius: 2 }}
              >
                {t('save')}
              </LoadingButton>
              <Link
                component={RouterLink}
                variant="body2"
                underline="hover"
                to="/dashboard/attendance/"
                sx={{ textAlign: 'center', display: 'block', color: 'text.secondary' }}
              >
                {t('go_back')}
              </Link>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {showPinDialog && (
        <PinDialog
          message={pinDialogMessage}
          open={showPinDialog}
          handleClose={() => setShowPinDialog(false)}
        />
      )}
    </FormProvider>
  );
}